(() => {
  if (customElements.get('jsca-badge')) return;
  customElements.define('jsca-badge', class extends HTMLElement {
    connectedCallback() {
      this.attempt ??= 0;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.start = this.querySelector('[data-badge-start]');
      this.viewport = this.querySelector('[data-badge-viewport]');
      this.fallback = this.querySelector('[data-badge-fallback]');
      this.controls = this.querySelector('[data-badge-controls]');
      this.status = this.querySelector('[data-badge-status]');
      this.pauseButton = this.querySelector('[data-badge-action="pause"]');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.visible = true;
      this.paused = false;
      this.start.hidden = false;
      this.start.disabled = false;
      this.dataset.state = 'static';
      this.start.addEventListener('click', () => this.load(), options);
      this.controls.addEventListener('click', event => {
        const action = event.target.closest('[data-badge-action]')?.dataset.badgeAction;
        if (action === 'pause') { this.paused = !this.paused; this.sync(); }
        else if (action) this.scene?.action(action);
      }, options);
      this.viewport.addEventListener('keydown', event => {
        if (!this.scene || event.altKey || event.ctrlKey || event.metaKey) return;
        const actions = { ArrowLeft: 'swing', ArrowRight: 'swing', Home: 'reset', Enter: 'flip', ' ': 'flip' };
        if (event.key === 'Escape') { this.scene.action('release'); this.paused = true; this.sync(); }
        else if (actions[event.key]) { event.preventDefault(); this.scene.action(actions[event.key], event.key === 'ArrowLeft' ? -1 : 1); }
      }, options);
      document.addEventListener('visibilitychange', () => this.sync(), options);
      this.motion.addEventListener('change', () => this.sync(), options);
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          this.visible = entries[0].isIntersecting; this.sync();
        }, { threshold: 0 });
        this.observer.observe(this);
      }
    }
    sync() {
      this.dataset.motion = this.motion.matches ? 'reduced' : 'full';
      this.dataset.paused = String(this.paused);
      this.pauseButton.textContent = this.paused ? this.dataset.resume : this.dataset.pause;
      this.pauseButton.hidden = this.motion.matches;
      this.scene?.update({ running: this.visible && !document.hidden && !this.paused, reduced: this.motion.matches });
      if (this.dataset.state === 'ready') this.status.textContent = this.motion.matches ? this.dataset.reduced : this.paused ? this.dataset.pausedMessage : this.dataset.ready;
    }
    fail() {
      if (!this.isConnected) return;
      this.attempt++;
      clearTimeout(this.deadline);
      this.scene?.destroy(); this.scene = null;
      this.dataset.state = 'error';
      this.fallback.hidden = false;
      this.viewport.hidden = true;
      this.viewport.removeAttribute('tabindex');
      this.controls.hidden = true;
      this.start.hidden = false; this.start.disabled = false;
      this.status.textContent = this.dataset.error;
    }
    async load() {
      if (this.dataset.state === 'loading' || this.scene) return;
      const attempt = ++this.attempt;
      delete this.dataset.failure;
      this.dataset.state = 'loading';
      this.start.disabled = true;
      this.status.textContent = this.dataset.loading;
      this.deadline = setTimeout(() => this.fail(), 25000);
      try {
        const { mountBadge } = await import(new URL(this.dataset.scene, document.baseURI).href);
        if (!this.isConnected || attempt !== this.attempt || this.dataset.state !== 'loading') return;
        this.viewport.hidden = false;
        const scene = await mountBadge(this.viewport, {
          svg: this.querySelector('[data-badge-wordmark] svg'),
          labels: { top: this.dataset.top, faith: this.dataset.faith, bottom: this.dataset.bottom },
          reduced: this.motion.matches,
          ready: () => {
            if (!this.isConnected || attempt !== this.attempt || this.dataset.state !== 'loading') return;
            clearTimeout(this.deadline);
            const keepFocus = document.activeElement === this.start;
            this.dataset.state = 'ready';
            this.fallback.hidden = true;
            this.start.hidden = true;
            this.controls.hidden = false;
            this.viewport.tabIndex = 0;
            this.sync();
            if (keepFocus) this.viewport.focus({ preventScroll: true });
          },
          fail: error => { if (attempt === this.attempt) { this.dataset.failure = error?.message || 'WebGL context lost'; this.fail(); } }
        });
        if (!this.isConnected || attempt !== this.attempt || this.dataset.state === 'error') scene.destroy();
        else { this.scene = scene; this.sync(); }
      } catch (error) {
        if (!this.isConnected || attempt !== this.attempt) return;
        console.warn('JSCA badge could not start:', error.name, error.message);
        this.dataset.failure = `${error.name}: ${error.message}`;
        this.fail();
      }
    }
    disconnectedCallback() {
      this.attempt++;
      this.abort?.abort(); this.observer?.disconnect(); clearTimeout(this.deadline);
      this.scene?.destroy(); this.scene = null;
      this.fallback.hidden = false; this.viewport.hidden = true;
      this.controls.hidden = true;
    }
  });
})();
